// src/components/pos/CompositeProductModal.js
import React  from 'react';
import { 
    Modal, Box, Typography, Button, TextField, Autocomplete, 
    Alert, CircularProgress, Paper, TableContainer, Table, 
    TableHead, TableRow, TableCell, TableBody, IconButton 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../../api/index.js';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 800 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const CompositeProductModal = ({ open, onClose, product, allProducts, onSave }) => {
    const [items, setItems] = React.useState([]);
    const [selectedItem, setSelectedItem] = React.useState(null);
    const [quantity, setQuantity] = React.useState(1);
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        const fetchItems = async () => {
            if (product?.id) {
                setLoading(true);
                try {
                    const response = await api.get(`/products/${product.id}/items`);
                    setItems(response.data);
                } catch (err) {
                    setError('No se pudieron cargar los componentes existentes.');
                } finally {
                    setLoading(false);
                }
            }
        };
        if (open) {
            fetchItems();
        } else {
            setItems([]); setError(''); setSelectedItem(null); setQuantity(1);
        }
    }, [open, product]);

    const handleAddItem = () => {
        if (!selectedItem || quantity <= 0) {
            setError('Selecciona un producto y una cantidad válida.');
            return;
        }
        if (items.some(i => i.product_id === selectedItem.id)) {
            setError('Este componente ya está en la lista.');
            return;
        }
        setItems([...items, { product_id: selectedItem.id, name: selectedItem.name, quantity: parseFloat(quantity) }]);
        setSelectedItem(null);
        setQuantity(1);
        setError('');
    };

    const handleRemoveItem = (productId) => {
        setItems(items.filter(i => i.product_id !== productId));
    };

    const handleSave = () => {
        const itemsToSave = items.map(({ product_id, quantity }) => ({
            item_product_id: product_id,
            quantity: parseFloat(quantity)
        }));
        onSave(itemsToSave);
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Typography variant="h6">Gestionar Componentes de: {product?.name}</Typography>
                {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Autocomplete
                        sx={{ flexGrow: 1 }}
                        options={allProducts.filter(p => !p.is_composite && p.id !== product?.id)}
                        getOptionLabel={(option) => `${option.name} (${option.sku})`}
                        value={selectedItem}
                        onChange={(e, newValue) => setSelectedItem(newValue)}
                        renderInput={(params) => <TextField {...params} label="Buscar Componente" />}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                    <TextField label="Cantidad" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} sx={{ width: 120 }} inputProps={{ step: "0.1" }} />
                    <Button variant="contained" onClick={handleAddItem} startIcon={<AddIcon />}>Añadir</Button>
                </Box>
                {loading ? <CircularProgress sx={{ mt: 2 }} /> : (
                    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 300 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Componente</TableCell>
                                    <TableCell align="right">Cantidad</TableCell>
                                    <TableCell align="right">Acción</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map(item => (
                                    <TableRow key={item.product_id}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell align="right">{item.quantity}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleRemoveItem(item.product_id)}><DeleteIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={onClose} sx={{ mr: 1 }}>Cancelar</Button>
                    <Button variant="contained" color="primary" onClick={handleSave}>Guardar Componentes</Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default CompositeProductModal;